/**
 * 主题管理的数据请求（TanStack Query），接口与参数与旧版页面一致
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { API_ENDPOINTS } from '@/api/endpoints'
import { themesApi, type ThemeSavePayload } from '@/api/services/themes'
import type { Theme, ThemeListFilters, ThemeUsageStats } from './types'

const THEMES_KEY = ['admin', 'themes'] as const

const EMPTY_STATS: ThemeUsageStats = {
  total_instances: 0,
  active_instances: 0,
  total_episodes: 0,
  avg_episodes_per_instance: 0
}

// 接口可能返回 { data: ... } 包装格式，也可能直接返回对象
function unwrap<T>(payload: any): T {
  return payload && typeof payload === 'object' && payload.data && !Array.isArray(payload.data) ? payload.data : payload
}

export function useThemeList(page: number, pageSize: number, filters: ThemeListFilters) {
  return useQuery({
    queryKey: [...THEMES_KEY, 'list', page, pageSize, filters],
    queryFn: async () => {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined))
      }
      const response = await apiClient.get(API_ENDPOINTS.THEMES.LIST, { params })
      return { themes: (response.data.themes ?? []) as Theme[], total: (response.data.total ?? 0) as number }
    },
    placeholderData: keepPreviousData
  })
}

export function useThemeDetail(id: string) {
  return useQuery({
    queryKey: [...THEMES_KEY, 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.THEMES.DETAIL(Number(id)))
      const theme = unwrap<Theme>(response.data)
      return theme && typeof theme.id === 'number' ? theme : null
    }
  })
}

// 使用统计；接口失败时显示 0，不影响详情展示
export function useThemeStats(id: number | string | undefined) {
  return useQuery({
    queryKey: [...THEMES_KEY, 'stats', id],
    enabled: id !== undefined,
    queryFn: async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.THEMES.STATS(Number(id)))
        return { ...EMPTY_STATS, ...unwrap<Partial<ThemeUsageStats>>(response.data) }
      } catch (error) {
        console.error('Failed to fetch theme stats:', error)
        return EMPTY_STATS
      }
    }
  })
}

export function useDeleteTheme() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.THEMES.DELETE(id)),
    // 后端删除是一次性的破坏性操作，不自动重试
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: THEMES_KEY })
  })
}

export function useSaveTheme() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id?: number; payload: ThemeSavePayload }) =>
      id === undefined ? themesApi.create(payload) : themesApi.update(id, payload),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: THEMES_KEY })
  })
}
